import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from "react";

export type ChartPlotLayout = {
  n: number;
  left: number;
  top: number;
  plotH: number;
  plotW: number;
  plotRight: number;
  baselineY: number;
  svgW: number;
  svgH: number;
  xAt: (i: number) => number;
};

export type ChartIndexRange = {
  startIndex: number;
  endIndex: number;
};

export type HoverSeries = {
  label: string;
  color: string;
  valueAt: (idx: number) => number;
  yAt: (idx: number) => number;
};

export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function indexFromPointerEvent(
  event: ReactPointerEvent<SVGElement>,
  chart: ChartPlotLayout,
): number | null {
  const rect = event.currentTarget.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const svgX = ((event.clientX - rect.left) / rect.width) * chart.svgW;
  const svgY = ((event.clientY - rect.top) / rect.height) * chart.svgH;
  if (
    svgX < chart.left ||
    svgX > chart.plotRight ||
    svgY < chart.top ||
    svgY > chart.baselineY
  ) {
    return null;
  }
  if (chart.n <= 1) return 0;
  const t = (svgX - chart.left) / chart.plotW;
  const index = clamp(Math.round(t * (chart.n - 1)), 0, chart.n - 1);
  return Number.isFinite(index) ? index : null;
}

export function sliceByIndexRange<T>(
  items: T[],
  range: ChartIndexRange | null,
): T[] {
  if (!range) {
    return items;
  }
  return items.slice(range.startIndex, range.endIndex + 1);
}

export function mergeViewRangeIntoAbsolute(
  viewRange: ChartIndexRange,
  parentRange: ChartIndexRange | null,
): ChartIndexRange {
  const offset = parentRange?.startIndex ?? 0;
  return {
    startIndex: offset + viewRange.startIndex,
    endIndex: offset + viewRange.endIndex,
  };
}

export function usePlotPointerInteraction(
  chart: ChartPlotLayout,
  options: {
    onRangeSelect?: (range: ChartIndexRange) => void;
  } = {},
) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [dragAnchor, setDragAnchor] = useState<number | null>(null);
  const [dragCurrent, setDragCurrent] = useState<number | null>(null);
  const dragAnchorRef = useRef<number | null>(null);
  const dragCurrentRef = useRef<number | null>(null);
  const isDragging = dragAnchor !== null;

  const previewRange =
    dragAnchor !== null && dragCurrent !== null
      ? {
          startIndex: Math.min(dragAnchor, dragCurrent),
          endIndex: Math.max(dragAnchor, dragCurrent),
        }
      : null;

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<SVGRectElement>) => {
      const index = indexFromPointerEvent(event, chart);
      if (index === null) {
        return;
      }
      event.currentTarget.setPointerCapture(event.pointerId);
      dragAnchorRef.current = index;
      dragCurrentRef.current = index;
      setDragAnchor(index);
      setDragCurrent(index);
      setHoverIndex(null);
    },
    [chart],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<SVGRectElement>) => {
      if (isDragging) {
        const index =
          indexFromPointerEvent(event, chart) ?? dragCurrentRef.current;
        dragCurrentRef.current = index;
        setDragCurrent(index);
        return;
      }
      setHoverIndex(indexFromPointerEvent(event, chart));
    },
    [chart, isDragging],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<SVGRectElement>) => {
      const anchor = dragAnchorRef.current;
      const current = dragCurrentRef.current;
      if (anchor !== null && current !== null && options.onRangeSelect) {
        const startIndex = Math.min(anchor, current);
        const endIndex = Math.max(anchor, current);
        if (
          startIndex !== endIndex &&
          Number.isFinite(startIndex) &&
          Number.isFinite(endIndex)
        ) {
          options.onRangeSelect({ startIndex, endIndex });
        }
      }
      dragAnchorRef.current = null;
      dragCurrentRef.current = null;
      setDragAnchor(null);
      setDragCurrent(null);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    },
    [options.onRangeSelect],
  );

  const onPointerLeave = useCallback(() => {
    if (!isDragging) {
      setHoverIndex(null);
    }
  }, [isDragging]);

  return {
    hoverIndex,
    previewRange,
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
  };
}

export function ChartRangeSelectionOverlay({
  chart,
  previewRange,
}: {
  chart: ChartPlotLayout;
  previewRange: ChartIndexRange | null;
}) {
  if (!previewRange) {
    return null;
  }
  const x1 = chart.xAt(previewRange.startIndex);
  const x2 = chart.xAt(previewRange.endIndex);
  const left = Math.min(x1, x2);
  const width = Math.max(Math.abs(x2 - x1), 1);
  return (
    <rect
      x={left}
      y={chart.top}
      width={width}
      height={chart.baselineY - chart.top}
      fill="hsl(var(--primary))"
      fillOpacity={0.12}
      stroke="hsl(var(--primary))"
      strokeOpacity={0.45}
      strokeWidth={1}
      pointerEvents="none"
    />
  );
}

export function ChartPlotInteractionRect({
  chart,
  interaction,
}: {
  chart: ChartPlotLayout;
  interaction: ReturnType<typeof usePlotPointerInteraction>;
}) {
  return (
    <rect
      x={chart.left}
      y={chart.top}
      width={chart.plotRight - chart.left}
      height={chart.baselineY - chart.top}
      fill="transparent"
      style={{ cursor: "crosshair" }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        interaction.onPointerDown(event);
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
        interaction.onPointerMove(event);
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
        interaction.onPointerUp(event);
      }}
      onPointerLeave={() => {
        interaction.onPointerLeave();
      }}
    />
  );
}

function tooltipSeriesForHover(
  series: HoverSeries[],
  hoverIndex: number,
  maxSeriesRows?: number,
): { visible: HoverSeries[]; hiddenCount: number } {
  if (!maxSeriesRows || series.length <= maxSeriesRows) {
    return { visible: series, hiddenCount: 0 };
  }
  const sorted = [...series].sort(
    (a, b) => (b.valueAt(hoverIndex) ?? 0) - (a.valueAt(hoverIndex) ?? 0),
  );
  return {
    visible: sorted.slice(0, maxSeriesRows),
    hiddenCount: sorted.length - maxSeriesRows,
  };
}

export function SharedHoverTooltip({
  chart,
  xLabel,
  hoverIndex,
  series,
  valueFormatter,
  maxSeriesRows,
}: {
  chart: ChartPlotLayout;
  xLabel: string;
  hoverIndex: number;
  series: HoverSeries[];
  valueFormatter: (n: number) => string;
  maxSeriesRows?: number;
}) {
  const hoverX = chart.xAt(hoverIndex);
  const { visible, hiddenCount } = tooltipSeriesForHover(
    series,
    hoverIndex,
    maxSeriesRows,
  );
  const tooltipPadding = 6;
  const rowH = 12;
  const tooltipW = 194;
  const footerRows = hiddenCount > 0 ? 1 : 0;
  const tooltipH = 18 + visible.length * rowH + footerRows * rowH;
  const tooltipX =
    hoverX + tooltipW + 8 <= chart.plotRight
      ? hoverX + 8
      : hoverX - tooltipW - 8;
  const tooltipY = Math.max(chart.top + 2, chart.top + 8);

  return (
    <g pointerEvents="none">
      <line
        x1={hoverX}
        y1={chart.top}
        x2={hoverX}
        y2={chart.baselineY}
        stroke="hsl(var(--foreground))"
        strokeOpacity={0.35}
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      {series.map((entry) => (
        <circle
          key={`hover-point-${entry.label}`}
          cx={hoverX}
          cy={entry.yAt(hoverIndex)}
          r={2.4}
          fill={entry.color}
        />
      ))}
      <rect
        x={tooltipX}
        y={tooltipY}
        width={tooltipW}
        height={tooltipH}
        rx={6}
        ry={6}
        fill="hsl(var(--background))"
        stroke="hsl(var(--border))"
      />
      <text
        x={tooltipX + tooltipPadding}
        y={tooltipY + 12}
        className="fill-foreground text-[7px]"
      >
        {xLabel}
      </text>
      {visible.map((entry, index) => {
        const y = tooltipY + 24 + index * rowH;
        const value = valueFormatter(entry.valueAt(hoverIndex));
        return (
          <g key={`hover-row-${entry.label}`}>
            <circle
              cx={tooltipX + tooltipPadding + 2}
              cy={y - 2.5}
              r={2}
              fill={entry.color}
            />
            <text
              x={tooltipX + tooltipPadding + 8}
              y={y}
              className="fill-muted-foreground text-[6.6px]"
            >
              {entry.label}
            </text>
            <text
              x={tooltipX + tooltipW - tooltipPadding}
              y={y}
              textAnchor="end"
              className="fill-foreground text-[6.6px]"
            >
              {value}
            </text>
          </g>
        );
      })}
      {hiddenCount > 0 ? (
        <text
          x={tooltipX + tooltipPadding}
          y={tooltipY + 24 + visible.length * rowH}
          className="fill-muted-foreground text-[6.6px]"
        >
          +{hiddenCount} more
        </text>
      ) : null}
    </g>
  );
}

export function ChartRangeResetButton({
  range,
  onReset,
  rangeLabel,
}: {
  range: ChartIndexRange | null;
  onReset: () => void;
  rangeLabel?: string;
}) {
  if (!range) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span>
        Showing{" "}
        {rangeLabel ??
          `points ${range.startIndex + 1}–${range.endIndex + 1}. Drag on a chart to adjust.`}
      </span>
      <button
        type="button"
        className="text-primary underline-offset-2 hover:underline"
        onClick={onReset}
      >
        Reset range
      </button>
    </div>
  );
}
